// ═════════════════════════════════════════════════════════════════════════════
//  MY SUBMISSIONS — the signed-in user's own submitted forms (upgrade QW 1.4)
//  Answers "did yesterday's tailgate go through?" without calling the office.
//  RLS scopes submissions SELECT to own rows for non-admins; this page is
//  deliberately own-rows only even for admins (the org-wide ledger lives in
//  the admin panel). PDFs re-download through short-lived signed URLs derived
//  from pdf_path; pdf_url is the fallback for legacy rows. Online-only, same
//  policy as My Actions.
// ═════════════════════════════════════════════════════════════════════════════

function _mySubsEsc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function openMySubmissionsPage() {
  showInnerPage('mySubsPage');
  _mySubsSyncHeader();
  loadMySubmissions();
}

// Project-first nav (Task 5): kicker+job-name header, same two-line pattern
// Project Documents already uses (index.html). Reads window.fieldProject
// live on every open, so Back → list → a different job never leaves the
// PREVIOUS job's name painted — no page-open ever skips this call.
function _mySubsSyncHeader() {
  const kicker = document.getElementById('mySubsKicker');
  const titleTxt = document.getElementById('mySubsTitleTxt');
  const hasCtx = !!(window.fieldProject && window.fieldProject.id);
  if (kicker) kicker.hidden = !hasCtx;
  if (titleTxt) titleTxt.textContent = hasCtx ? window.fieldProject.name : 'My Submissions';
}

async function loadMySubmissions() {
  const wrap = document.getElementById('mySubsList');
  if (!wrap || !window.currentUser) return;
  wrap.innerHTML = '<div class="docs-loading">Loading…</div>';

  let q = sbClient.from('submissions')
    .select('id, submission_type, project_name, submitted_at, locked_at, pdf_path, pdf_url')
    .eq('foreman_id', window.currentUser.id);
  // Project-first nav (Task 5): scoped to the job space when one is picked —
  // the header (see _mySubsSyncHeader) already reads as that job's
  // submissions, so the list matches it. No context (list screen / zero-job
  // org) → every submission, exactly as before this task — old data, old
  // behavior, nothing new to break.
  if (window.fieldProject && window.fieldProject.id) {
    q = q.eq('project_name', window.fieldProject.name);
  }
  const { data, error } = await q
    .order('submitted_at', { ascending: false })
    .limit(100);

  if (error) {
    wrap.innerHTML = `
      <div class="fx-empty">
        <div class="fx-empty-t">Couldn't load your submissions — you may be offline.</div>
        <button class="fx-retry" onclick="loadMySubmissions()">Retry</button>
      </div>`;
    return;
  }
  if (!data || !data.length) {
    wrap.innerHTML = `
      <div class="fx-empty">
        <span class="fp-empty-ic" aria-hidden="true"><i data-lucide="history"></i></span>
        <div class="fx-empty-t">No submissions yet</div>
        <div class="fx-empty-s">Forms you submit show up here with their PDFs.</div>
      </div>`;
    return;
  }

  // Group by local calendar date; rows arrive newest-first so groups stay sorted.
  const fmtDay  = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const fmtTime = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
  const groups = [];
  let cur = null;
  for (const r of data) {
    const d = new Date(r.submitted_at);
    const key = d.toDateString();
    if (!cur || cur.key !== key) { cur = { key, label: fmtDay.format(d), rows: [] }; groups.push(cur); }
    cur.rows.push(r);
  }

  // Redesign v3: one row per record, in the field app's row vocabulary — the
  // inline styles this used to carry were literals of the old dark palette and
  // beat every stylesheet, so the screen stayed dark inside a light app.
  const card = (r) => {
    const t = fmtTime.format(new Date(r.submitted_at));
    const lock = r.locked_at
      ? ' <span class="fp-lock" title="Signed — locked" aria-label="Signed — locked"><i data-lucide="lock"></i></span>'
      : '';
    // Always show the PDF affordance — a missing button beside an identical
    // neighbour read as random breakage (gauntlet 2026-08-08). Site Photos
    // Only never has a PDF by construction (handleSitePhotosUpload inserts the
    // row with none), so its greyed button read as "failed": say "Photos".
    const pdfBtn = r.submission_type === 'Site Photos Only'
      ? `<span class="fx-chip">${_mySubsEsc(window.Ordinex?.i18n?.t('mysubs.photosOnly') ?? 'Photos')}</span>`
      : (r.pdf_path || r.pdf_url)
      ? `<button onclick="downloadMySubmissionPdf('${r.id}')" class="fp-pdf-btn"
           aria-label="Download PDF"><i data-lucide="download"></i>PDF</button>`
      : `<button class="fp-pdf-btn" disabled title="No PDF on file for this record"
           aria-label="No PDF on file"><i data-lucide="download"></i>PDF</button>`;
    // Scoped to one job → its name is already the page title; repeated per
    // row it only wrapped the time onto a second line on phones.
    const sub = (window.fieldProject && window.fieldProject.id)
      ? t : `${_mySubsEsc(r.project_name || '')} · ${t}`;
    return `
    <div class="fx-row fx-row--static">
      <span class="fx-row-txt">
        <span class="fx-row-name">${_mySubsEsc(r.submission_type)}${lock}</span>
        <span class="fx-row-sub">${sub}</span>
      </span>
      ${pdfBtn}
    </div>`;
  };

  window._mySubsRows = data;   // downloadMySubmissionPdf looks rows up by id
  // The day header carries its own count, so a long list says how long each
  // day was without the foreman counting rows.
  wrap.innerHTML = groups.map(g => `
    <div class="fx-dayhead"><span>${_mySubsEsc(g.label)}</span><span class="fx-dayhead-n">${g.rows.length}</span></div>
    <div class="fx-rows">${g.rows.map(card).join('')}</div>`).join('');
  if (window.lucide) lucide.createIcons();
}

async function downloadMySubmissionPdf(id) {
  const r = (window._mySubsRows || []).find(x => x.id === id);
  if (!r) return;
  if (r.pdf_path) {
    const { data } = await sbClient.storage.from('form-submissions').createSignedUrl(r.pdf_path, 3600);
    if (data?.signedUrl) { window.open(data.signedUrl, '_blank', 'noopener'); return; }
  }
  const stored = window.Ordinex.docUrls.safeUrl(r.pdf_url);
  if (stored) { window.open(stored, '_blank', 'noopener'); return; }
  if (typeof showToast === 'function') showToast('PDF is not available for this record.', 'warning');
}
